import React, { useEffect, useState } from "react";
import moment from "moment";
// v1 date range picker, by design choice.
import {
  colors2,
  DateRangePicker,
  LoaderOverlay,
  UI_CAPTION_SEMIBOLD,
} from "oolib";
import {
  ButtonPrimary,
  ButtonTertiary,
} from "oolib/dist/v2/components/Buttons";
import { DropdownMulti } from "oolib/dist/v2/components/Dropdowns/DropdownMulti";
import { useQueryClient } from "react-query";
import {
  externalConnectionKey,
  useExternalConnectionStatus,
} from "@/utils/react-query-hooks/externalConnections";
import { useFetchPreflightData } from "../../DirectDataImport/hooks/useFetchPreflightData";
import { IntegrationDisconnectConfirmModal } from "../IntegrationDisconnectConfirmModal";
import { IntegrationModalShell } from "../comps/IntegrationModalShell";
import { ConnectionCard } from "../comps/ConnectionCard";
import { EditImportSettings } from "../comps/EditImportSettings";
import { caption, isoDaysAgo } from "../comps/common";
import { ManageIntegrationModal } from "../comps/ManageIntegrationModal";

/**
 * Slack's popups, on IntegrationModalShell.
 *
 * ImportModal is "Import from Slack": the connected workspace, channels and a
 * date range. All picked channels go into one dataset (the only mode for now).
 * It is the import wizard's Slack step (DirectDataImport renders it for a
 * connected Slack): Continue hands the adapter options to onContinue, and the
 * wizard fetches and moves on to field mapping, passing back isLoading / error.
 *
 * ManageModal is the connection and the datasets Slack imports into;
 * "Configure" on a dataset swaps to its sync settings, with a back arrow.
 */

// Workspace channels, once the connection is known to be usable. On failure,
// refetch the status: a 401 here marks the connection stale server-side, which
// hands the wizard back to its reconnect step.
const useSlackChannels = (conn) => {
  const qc = useQueryClient();
  const {
    mutate: fetchPreflight,
    data,
    isLoading,
    error,
  } = useFetchPreflightData();
  const isUsable = conn?.connected && conn.status !== "stale";
  useEffect(() => {
    if (!isUsable) return;
    fetchPreflight(
      { sourceId: "slack" },
      { onError: () => qc.invalidateQueries(externalConnectionKey("slack")) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsable]);
  const options = (data?.preflightData?.channels || []).map((ch) => ({
    display: `#${ch.name}${ch.isPrivate ? " (private)" : ""}`,
    value: ch.id,
  }));
  const errorMsg = error && (error.message || "Failed to load Slack data.");
  return { options, isLoading, errorMsg };
};

const todayRange = () => [isoDaysAgo(0), isoDaysAgo(0)];

// Same options the import wizard's old Slack step sent.
const toAdapterOptions = (channels, [startDate, endDate]) => ({
  channelIds: channels.map((c) => c.value),
  oldest: moment(startDate).startOf("day").unix(),
  latest: moment(endDate).endOf("day").unix(),
});

// Channel picker and date range, shared by the import popup and "Edit import
// settings". inCard: inside a card, so the dropdown pops out of its clipping.
const SlackFields = ({
  options,
  isLoadingChannels,
  channels,
  setChannels,
  dateRange,
  setDateRange,
  readOnly,
  inCard,
}) => (
  <>
    <DropdownMulti
      id="channels"
      label="Select channels"
      placeholder={
        isLoadingChannels
          ? "Loading channels..."
          : "Select one or more channels"
      }
      options={options}
      value={channels}
      onChange={(_k, v) => setChannels(v || [])}
      disabled={isLoadingChannels}
      readOnly={readOnly}
      isSearchable
      searchbarPlaceholder="Search channels..."
      popOutOfOverflowHiddenParent={inCard}
    />
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <DateRangePicker
        id="dateRange"
        label="Date range"
        value={dateRange}
        onChange={(_k, v) => setDateRange(v)}
        previousDateOnly
        readOnly={readOnly}
      />
      {caption("Larger ranges may take longer or fail to import.")}
    </div>
  </>
);

function ImportModal({
  Icon,
  onClose,
  onBack,
  onContinue,
  isLoading,
  error,
  hideBackdrop,
  fromManage,
  initialOptions,
}) {
  const { data: conn } = useExternalConnectionStatus("slack");
  const {
    options,
    isLoading: isLoadingChannels,
    errorMsg: channelsError,
  } = useSlackChannels(conn);
  const [channels, setChannels] = useState([]);
  // initialOptions: picks already made elsewhere (and being fetched), shown
  // here so a failed fetch can be adjusted and retried.
  const [dateRange, setDateRange] = useState(() =>
    initialOptions
      ? [
          moment.unix(initialOptions.oldest).format("YYYY-MM-DD"),
          moment.unix(initialOptions.latest).format("YYYY-MM-DD"),
        ]
      : todayRange(),
  );
  useEffect(() => {
    if (initialOptions && options.length && !channels.length)
      setChannels(
        options.filter((o) => initialOptions.channelIds.includes(o.value)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);
  const [showDisconnect, setShowDisconnect] = useState(false);

  const [startDate, endDate] = dateRange;
  const canContinue = channels.length > 0 && startDate && endDate;

  return (
    <IntegrationModalShell
      title="Import from Slack"
      Icon={Icon}
      onBack={onBack}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      footer={
        <>
          <ButtonTertiary onClick={onClose}>
            {fromManage ? "Discard" : "Do Later"}
          </ButtonTertiary>
          <ButtonPrimary
            onClick={() => onContinue(toAdapterOptions(channels, dateRange))}
            disabled={!canContinue || isLoadingChannels || isLoading}
            isLoading={isLoading}
          >
            Continue
          </ButtonPrimary>
        </>
      }
    >
      {isLoading && (
        <LoaderOverlay loaderText="Fetching data from external source..." />
      )}
      {/* Left out when launched from the manage popup, which shows it. */}
      {!fromManage && (
        <ConnectionCard
          account={conn?.providerAccountName || "Slack"}
          email={conn?.adminEmail}
          disconnectDisabled={isLoading}
          onDisconnect={() => setShowDisconnect(true)}
        />
      )}
      {showDisconnect && (
        <IntegrationDisconnectConfirmModal
          provider="slack"
          name="Slack"
          onClose={() => setShowDisconnect(false)}
          // Nothing to import from once disconnected.
          onDisconnected={onClose}
        />
      )}
      {caption(
        "Import Slack messages into your dataset. Each message becomes a row, with threaded replies combined into the parent message.",
      )}
      <SlackFields
        options={options}
        isLoadingChannels={isLoadingChannels}
        channels={channels}
        setChannels={setChannels}
        dateRange={dateRange}
        setDateRange={setDateRange}
        readOnly={isLoading}
      />
      {[channelsError, error].filter(Boolean).map((msg) =>
        // Nothing in the range is a notice, not a failure.
        /no slack messages found/i.test(msg) ? (
          <React.Fragment key={msg}>{caption(msg)}</React.Fragment>
        ) : (
          <UI_CAPTION_SEMIBOLD key={msg} style={{ color: colors2.red }}>
            {msg}
          </UI_CAPTION_SEMIBOLD>
        ),
      )}
    </IntegrationModalShell>
  );
}

// "Edit import settings": pick channels and a range to import more messages
// into this dataset. Save runs them through the import wizard, which fetches
// straight away, maps fields and appends. Starts from the dataset's last
// channels.
const EditSlackImport = ({ conn, dataset, onSave }) => {
  const { options, isLoading } = useSlackChannels(conn);
  const [channels, setChannels] = useState([]);
  const [dateRange, setDateRange] = useState(todayRange);
  useEffect(() => {
    const ids = dataset.scope.channelIds || [];
    if (options.length && !channels.length)
      setChannels(options.filter((o) => ids.includes(o.value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);
  return (
    <EditImportSettings
      saveDisabled={channels.length === 0}
      onSave={() => onSave(toAdapterOptions(channels, dateRange))}
    >
      <SlackFields
        options={options}
        isLoadingChannels={isLoading}
        channels={channels}
        setChannels={setChannels}
        dateRange={dateRange}
        setDateRange={setDateRange}
        inCard
      />
    </EditImportSettings>
  );
};

// The shared manage popup, with Slack's "Edit import settings".
const ManageModal = (props) => (
  <ManageIntegrationModal
    provider="slack"
    EditImport={EditSlackImport}
    {...props}
  />
);

export const slack = {
  ImportModal,
  ManageModal,
};
